/* ─────────────────────────────────────────────────────────────────
   DEVOPS — MODULE-BASED COURSE CONTENT
   Imported by MicrointernshipTask.jsx

   Some tasks here are pure code (GitHub link only); others need a
   screenshot too (a green CI run, a live pipeline, a dashboard) since
   that's the only way to prove a pipeline actually executed — set
   per-topic via `submissionType`.
───────────────────────────────────────────────────────────────── */

export const DEVOPS_MODULES = [
  {
    module: 1,
    title: "Version Control & Continuous Integration",
    emoji: "🔀",
    topics: [
      {
        id: 1,
        topic: "Git Workflows & Branching Strategies",
        description: "Learn professional Git workflows — feature branches, pull requests, and merge strategies used by real teams.",
        motivation: "Great start! Clean Git history is a habit every team lead notices immediately. 🔀",
        resource: {
          name: "Atlassian — Git Branching Strategies",
          url: "https://www.atlassian.com/git/tutorials/comparing-workflows",
        },
        task: "Simulate a real team workflow on a small project using feature branches and pull requests.",
        requirements: [
          "Create at least 3 feature branches, each for a separate small change",
          "Open a pull request for each branch with a clear description",
          "Resolve at least one merge conflict intentionally created between branches",
          "Use meaningful, conventional commit messages throughout",
          "Tag a release (e.g. v1.0.0) once all branches are merged to main",
        ],
      },
      {
        id: 2,
        topic: "Continuous Integration with GitHub Actions",
        description: "Learn to automatically run tests and checks on every push using GitHub Actions.",
        motivation: "Your code checks itself now! CI is what stops broken code from ever reaching production. ✅",
        resource: {
          name: "GitHub Docs — Understanding GitHub Actions",
          url: "https://docs.github.com/en/actions/learn-github-actions/understanding-github-actions",
        },
        task: "Add a GitHub Actions workflow that runs tests/lint checks on every push and pull request.",
        requirements: [
          "Workflow file (.github/workflows/ci.yml) triggered on push and pull_request",
          "Runs install, lint, and test steps for the project",
          "Fails the workflow clearly if any step fails",
          "Add a status badge to your README.md showing pipeline status",
          "Take a screenshot of a successful (green) workflow run in the Actions tab",
        ],
        submissionType: "both",
      },
    ],
  },
  {
    module: 2,
    title: "Containerization",
    emoji: "🐳",
    topics: [
      {
        id: 3,
        topic: "Docker Fundamentals",
        description: "Learn to containerize an application so it runs identically on any machine.",
        motivation: "Containerized! Your app now runs the same everywhere — this is a core modern DevOps skill. 🐳",
        resource: {
          name: "Docker Docs — Get Started",
          url: "https://docs.docker.com/get-started/",
        },
        task: "Write a Dockerfile for an existing application and run it in a container.",
        requirements: [
          "Multi-stage Dockerfile if applicable (smaller final image)",
          "Container starts and serves the app correctly on a mapped port",
          "Use a .dockerignore file to keep the image lean",
          "Document build and run commands in the README",
          "Include the Dockerfile in your GitHub repository",
        ],
      },
      {
        id: 4,
        topic: "Docker Compose for Multi-Container Apps",
        description: "Learn to orchestrate multiple containers (app + database) together using Docker Compose.",
        motivation: "Multiple services, one command to run them all — you're thinking in real infrastructure now. 🧩",
        resource: {
          name: "Docker Docs — Docker Compose Overview",
          url: "https://docs.docker.com/compose/",
        },
        task: "Set up a docker-compose.yml running your app alongside a database container.",
        requirements: [
          "App and database defined as separate services",
          "Environment variables used for DB connection (not hardcoded)",
          "A named volume so database data persists across restarts",
          "`docker compose up` starts the entire stack successfully",
          "Take a screenshot of `docker ps` showing both containers running",
        ],
        submissionType: "both",
      },
    ],
  },
  {
    module: 3,
    title: "Deployment & Observability",
    emoji: "🚀",
    topics: [
      {
        id: 5,
        topic: "CI/CD Pipeline — Automated Build, Test & Deploy",
        description: "Learn to extend CI into full CD — automatically deploying your app whenever main is updated.",
        motivation: "Push to main, and it's live. That's the real payoff of everything you've built so far. 🚀",
        resource: {
          name: "GitHub Docs — Deploying with GitHub Actions",
          url: "https://docs.github.com/en/actions/deployment/about-deployments/deploying-with-github-actions",
        },
        task: "Extend your CI workflow into a full CI/CD pipeline that deploys automatically on push to main.",
        requirements: [
          "Pipeline stages: install → test → build → deploy",
          "Deploys to a real host (Render, Railway, Vercel, or similar) on push to main",
          "Secrets (API keys, tokens) stored in GitHub Actions secrets, never hardcoded",
          "Pipeline fails and blocks deployment if tests fail",
          "Take a screenshot of a completed deployment run and the live app URL",
        ],
        submissionType: "both",
      },
      {
        id: 6,
        topic: "Monitoring & Logging Basics",
        description: "Learn to add basic logging and monitoring so you can see what your deployed app is actually doing.",
        motivation: "You can now see inside your running app — this is what makes debugging production issues possible. 📊",
        resource: {
          name: "Grafana Docs — Getting Started",
          url: "https://grafana.com/docs/grafana/latest/getting-started/",
        },
        task: "Add structured logging to your app and set up a basic monitoring/uptime check.",
        requirements: [
          "Structured request logging (method, path, status, response time)",
          "Centralized error logging (not just console.log scattered everywhere)",
          "Set up a free uptime monitor (e.g. UptimeRobot) pointed at your deployed app",
          "Add a /health endpoint the monitor checks",
          "Take a screenshot of the monitoring dashboard showing your app as 'up'",
        ],
        submissionType: "both",
      },
    ],
  },
  {
    module: 4,
    title: "Final Project",
    emoji: "🏆",
    topics: [
      {
        id: 7,
        topic: "End-to-End CI/CD Pipeline for a Full Application",
        description: "Apply everything learned throughout the MicroIntern to ship a fully automated pipeline for a real application.",
        motivation: "Incredible! You built a complete DevOps pipeline from Git commit to live, monitored deployment. That's certificate-worthy. 🎉",
        resource: {
          name: "GitHub Actions Documentation",
          url: "https://docs.github.com/en/actions",
        },
        task: "Combine everything into one project: a Dockerized app with a full CI/CD pipeline and basic monitoring.",
        requirements: [
          "Application containerized with Docker (and Docker Compose if it uses a database)",
          "GitHub Actions pipeline: lint/test → build → deploy on push to main",
          "Automated deployment to a live, publicly reachable URL",
          "Secrets managed securely, never committed to the repo",
          "A /health endpoint monitored by an uptime checker",
          "Take a screenshot of: the green pipeline run, the live app, and the monitoring dashboard",
          "A README.md documenting the full pipeline architecture",
        ],
        submissionType: "both",
      },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────
   Flat array — used by COURSE_TASKS in MicrointernshipTask.jsx
   Preserves backward-compatible indexing for completedTopics[]
───────────────────────────────────────────────────────────────── */
export const DEVOPS_TASKS = DEVOPS_MODULES.flatMap((m) => m.topics);
